import { Alert, Button, Select } from '@mantine/core';
import axios from 'axios';
import { getHttpErrorMessage } from '@/services/httpRetry.ts';
import type { ActivationFence } from '@/types/automationPublications.ts';
import {
  useAutomationActivation,
  usePublicationIntegrity,
} from '@/hooks/api/automations/useAutomationPublications.ts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DatasetType } from '@/types/automationFields.ts';
import useTriggerManualRun, {
  ManualRunInputProcessingError,
  TriggerDatasetType,
  TriggerInputDataset,
} from '@/hooks/api/datasets/trigger/useTriggerManualRun.ts';
import useAutomationVersionErrors from '@/hooks/useAutomationVersionErrors.ts';
import useRetrieveAutomations from '@/hooks/api/automations/useRetrieveAutomations.ts';
import useRetrieveAutomation from '@/hooks/api/automations/useRetrieveAutomation.ts';
import useRetrieveIntegrationActionForStep from '@/hooks/api/integrations/useRetrieveIntegrationActionForStep';
import useTriggerStep from './hooks/useTriggerStep';
import {
  ManualTriggerInput,
  ManualTriggerInputType,
  TriggerType,
} from '@/types/automationSteps.ts';
import { notifications } from '@mantine/notifications';
import { reportException } from '@/services/sentry.ts';
import ManualRunModalUI, {
  ManualRunTab,
} from '@/pages/AutomationPage/components/ManualRunModal/ManualRunModalUI.tsx';
import { Automation } from '@/types/automations.ts';
import { RunWithDatasets } from '@/types/runs.ts';
import {
  analyzeTriggerDatasetCompatibility,
  applyRunInputsToTriggerDatasets,
  type TriggerDatasetCompatibility,
} from '@/pages/AutomationPage/components/ManualRunModal/utils/triggerDatasetMatching.ts';

type props = {
  opened: boolean;
  onClose: () => void;
  automation?: Automation;
  seedRun?: RunWithDatasets;
};

const ManualRunModal = ({ opened, onClose, automation, seedRun }: props) => {
  const { data: automations } = useRetrieveAutomations();

  const [selectedAutomationId, setSelectedAutomationId] = useState<string | undefined>(undefined);

  const [ignoreCache, setIgnoreCache] = useState<boolean>(false);

  const automationId = automation?.id || selectedAutomationId;

  const { data: currentAutomation, refetch: refreshAutomation } =
    useRetrieveAutomation(automationId);

  const publishedVersion = useMemo(
    () => currentAutomation?.published_version || undefined,
    [currentAutomation],
  );

  const activation = useAutomationActivation(automationId);
  const [reviewedActivation, setReviewedActivation] = useState<ActivationFence | null>(null);
  const [selectedTrigger, setSelectedTrigger] = useState<string | null>(null);
  useEffect(() => {
    setSelectedTrigger(null);
  }, [automationId]);
  const matchingSelection =
    Boolean(activation.data?.published_version_id) &&
    activation.data?.automation_id === automationId &&
    activation.data?.active_roots.every(
      (root) => root.automation_version_id === publishedVersion?.id,
    );
  useEffect(() => {
    if (
      opened &&
      !activation.isFetching &&
      !activation.isError &&
      activation.isFetchedAfterMount &&
      matchingSelection &&
      (!reviewedActivation || reviewedActivation.automation_id !== automationId)
    )
      setReviewedActivation(activation.data!);
  }, [
    opened,
    activation.data,
    activation.isFetching,
    activation.isError,
    activation.isFetchedAfterMount,
    matchingSelection,
    reviewedActivation,
    automationId,
  ]);
  const applicableRoots =
    reviewedActivation && reviewedActivation.automation_id === automationId
      ? reviewedActivation.active_roots
      : [];
  const triggerId =
    applicableRoots.length === 1
      ? applicableRoots[0].trigger_step_id
      : (selectedTrigger ?? undefined);
  const triggerStep = useTriggerStep(publishedVersion, triggerId);
  const integrity = usePublicationIntegrity(automationId, opened && Boolean(triggerId), triggerId);
  const selectionFresh = Boolean(
    reviewedActivation &&
    reviewedActivation.automation_id === automationId &&
    matchingSelection &&
    !activation.isError &&
    !activation.isFetching &&
    JSON.stringify(reviewedActivation) === JSON.stringify(activation.data),
  );
  const publicationHealthy = Boolean(
    integrity.isFetchedAfterMount &&
    !integrity.isError &&
    !integrity.isFetching &&
    integrity.data?.status === 'healthy' &&
    integrity.data.automation_id === automationId &&
    integrity.data.trigger_step_id === triggerId &&
    integrity.data.head_revision === activation.data?.head_revision &&
    integrity.data.published_version_id === activation.data?.published_version_id &&
    integrity.data.published_version_id === publishedVersion?.id,
  );

  const { data: triggerAction } = useRetrieveIntegrationActionForStep(automationId, triggerStep);

  const loadingTriggerConfig =
    !triggerStep ||
    (triggerStep.trigger_type === TriggerType.External && triggerAction === undefined);

  const triggers: ManualTriggerInput[] = useMemo(() => {
    if (!triggerStep) {
      return [];
    } else if (triggerStep.trigger_type === TriggerType.Manual) {
      return triggerStep.manual_trigger_inputs;
    } else if (triggerAction) {
      return [
        {
          output_id: '',
          name: 'Manual Trigger for ' + triggerAction.display_name,
          description: '',
          input_type:
            triggerAction.output_type === DatasetType.Directory
              ? ManualTriggerInputType.Folder
              : ManualTriggerInputType.File,
          is_required: true,
        },
      ];
    }
    return [];
  }, [triggerAction, triggerStep]);

  const errors = useAutomationVersionErrors(publishedVersion);

  const versionHasErrors = errors.length > 0;

  const formDisabled =
    !publishedVersion ||
    !selectionFresh ||
    !publicationHealthy ||
    !triggerId ||
    versionHasErrors ||
    !triggerStep ||
    (triggerStep.trigger_type === TriggerType.External && !triggerAction);

  const [runName, setRunName] = useState<string>('');
  const [activeTab, setActiveTab] = useState<ManualRunTab>('new');

  const [triggerDatasets, setTriggerDatasets] = useState<TriggerInputDataset[]>([]);
  const [seedCompatibility, setSeedCompatibility] = useState<TriggerDatasetCompatibility | null>(
    null,
  );
  const [appliedSeedKey, setAppliedSeedKey] = useState<string | null>(null);

  const resetTriggerDatasets = useCallback(() => {
    const tDatasets: TriggerInputDataset[] = triggers.map((t) => {
      const common = { trigger_id: t.output_id || '' };
      switch (t.input_type) {
        case ManualTriggerInputType.File:
          return { ...common, type: TriggerDatasetType.File, file: undefined };
        case ManualTriggerInputType.Folder:
          return { ...common, type: TriggerDatasetType.Directory, files: [], name: '' };
        case ManualTriggerInputType.Text:
          return { ...common, name: '', type: TriggerDatasetType.Text, text: '' };
        case ManualTriggerInputType.Json:
          return { ...common, type: TriggerDatasetType.Json, text: '' };
        default:
          throw new Error('Unsupported manual trigger input type.');
      }
    });
    setTriggerDatasets(tDatasets);
  }, [triggers]);

  useEffect(() => {
    if (publishedVersion?.id) {
      resetTriggerDatasets();
    }
  }, [publishedVersion?.id, resetTriggerDatasets]);

  const seedKey = useMemo(() => {
    if (!seedRun || !publishedVersion?.id) {
      return null;
    }
    return `${seedRun.run.id}:${publishedVersion.id}`;
  }, [publishedVersion?.id, seedRun]);

  const updateTriggerDataset = useCallback((index: number, updatedDataset: TriggerInputDataset) => {
    setTriggerDatasets((prev) => {
      const newDatasets = [...prev];
      newDatasets[index] = updatedDataset;
      return newDatasets;
    });
  }, []);

  const applyPreviousRun = useCallback(
    (run: RunWithDatasets, matchType: 'perfect' | 'partial') => {
      setTriggerDatasets((prev) =>
        applyRunInputsToTriggerDatasets({
          currentTriggerDatasets: prev,
          triggers,
          inputDatasets: run.input_datasets,
        }),
      );

      setRunName(run.run.name);
      setSeedCompatibility(null);

      if (matchType === 'partial') {
        setActiveTab('new');
      }
    },
    [triggers],
  );

  useEffect(() => {
    if (
      !opened ||
      !seedRun ||
      !seedKey ||
      formDisabled ||
      appliedSeedKey === seedKey ||
      triggerDatasets.length !== triggers.length
    ) {
      return;
    }

    const compatibility = analyzeTriggerDatasetCompatibility({
      triggers,
      inputDatasets: seedRun.input_datasets,
    });

    setTriggerDatasets(
      applyRunInputsToTriggerDatasets({
        currentTriggerDatasets: triggerDatasets,
        triggers,
        inputDatasets: seedRun.input_datasets,
      }),
    );
    setRunName(seedRun.run.name);
    setActiveTab('new');
    setSeedCompatibility(compatibility);
    setAppliedSeedKey(seedKey);
  }, [appliedSeedKey, formDisabled, opened, seedKey, seedRun, triggerDatasets, triggers]);

  const resetForm = useCallback(() => {
    setReviewedActivation(null);
    setSelectedTrigger(null);
    setRunName('');
    setActiveTab('new');
    setSeedCompatibility(null);
    setAppliedSeedKey(null);
    resetTriggerDatasets();
    onClose();
  }, [onClose, resetTriggerDatasets]);

  const { mutate: createManualRun, isPending: creatingRun } = useTriggerManualRun(automationId, {
    onSuccess: () => resetForm(),
    onError: (error) => {
      if (error instanceof ManualRunInputProcessingError) {
        return;
      }
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        void activation.refetch();
        void refreshAutomation();
      }
      void integrity.refetch();
      reportException(error);
      notifications.show({
        title: 'Error creating run',
        message: getHttpErrorMessage(error),
        color: 'red',
      });
    },
  });

  const submitDisabled = useMemo(() => {
    if (!runName) return true;
    if (triggerDatasets.length !== triggers.length) return true;
    const invalid = triggerDatasets.map((td, idx) => {
      const trigger = triggers[idx];
      if (td.type === TriggerDatasetType.Json) {
        if (!td.text.trim()) return trigger.is_required;
        try {
          const parsed: unknown = JSON.parse(td.text);
          return !parsed || typeof parsed !== 'object' || Array.isArray(parsed);
        } catch {
          return true;
        }
      }
      if (!trigger.is_required) return false;
      if (td.type === TriggerDatasetType.Text && !td.text) return true;
      if (td.type === TriggerDatasetType.File && !td.file) return true;
      if (td.type === TriggerDatasetType.Directory && !td.files) return true;
      return false;
    });
    return invalid.includes(true);
  }, [runName, triggerDatasets, triggers]);

  const onConfirm = useCallback(() => {
    if (formDisabled || !selectionFresh || !reviewedActivation || !triggerId) return;
    createManualRun({
      expectedActivation: reviewedActivation,
      triggerStepID: triggerStep!.step_id,
      runName: runName,
      triggers: triggerDatasets,
      executionConfig: { ignore_cache: ignoreCache, run_downstream: true },
    });
  }, [
    createManualRun,
    ignoreCache,
    runName,
    triggerDatasets,
    triggerStep,
    selectionFresh,
    reviewedActivation,
    triggerId,
    formDisabled,
  ]);

  const handleAttemptClose = useCallback(() => {
    if (creatingRun) {
      notifications.show({
        title: 'Please wait',
        message: 'Your trigger inputs are still being uploaded.',
        color: 'yellow',
      });
      return;
    }

    resetForm();
  }, [creatingRun, resetForm]);

  useEffect(() => {
    if (!creatingRun) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [creatingRun]);

  const refreshPublicationSelection = async () => {
    const [next] = await Promise.allSettled([
      activation.refetch(),
      refreshAutomation(),
      integrity.refetch(),
    ]);
    if (next.status === 'fulfilled' && next.value.data && !next.value.isError) {
      setReviewedActivation(next.value.data);
      setSelectedTrigger(null);
    }
  };

  return (
    <ManualRunModalUI
      selectionControls={
        <>
          {triggerId && !publicationHealthy && (
            <Alert
              color={integrity.data?.status === 'invalid' ? 'red' : 'orange'}
              title="Publication needs verification"
            >
              {integrity.data?.findings[0]?.message ??
                'Verify the active publication before creating a run.'}
              <Button
                variant="subtle"
                disabled={creatingRun}
                onClick={() => void refreshPublicationSelection()}
              >
                Refresh publication health
              </Button>
            </Alert>
          )}
          {applicableRoots.length > 1 && (
            <Select
              label="Published trigger"
              value={selectedTrigger}
              disabled={creatingRun}
              data={applicableRoots.map((root) => ({
                value: root.trigger_step_id,
                label: root.trigger_step_id,
              }))}
              onChange={setSelectedTrigger}
            />
          )}
          {!selectionFresh && (
            <Alert color="orange">
              Published selection changed or could not be verified.
              <Button
                variant="subtle"
                disabled={creatingRun}
                onClick={() => void refreshPublicationSelection()}
              >
                Refresh and review selection
              </Button>
            </Alert>
          )}
        </>
      }
      automations={automations}
      selectedAutomationId={selectedAutomationId}
      setSelectedAutomationId={setSelectedAutomationId}
      opened={opened}
      onClose={handleAttemptClose}
      automation={automation}
      creatingRun={creatingRun}
      errors={errors}
      triggerStep={triggerStep}
      loadingTriggerConfig={loadingTriggerConfig}
      formDisabled={formDisabled}
      runName={runName}
      setRunName={setRunName}
      triggers={triggers}
      triggerDatasets={triggerDatasets}
      updateTriggerDataset={updateTriggerDataset}
      onConfirm={onConfirm}
      automationId={automationId}
      submitDisabled={submitDisabled}
      ignoreCache={ignoreCache}
      setIgnoreCache={setIgnoreCache}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      seedRun={seedRun}
      seedCompatibility={seedCompatibility}
      onSelectPerfectRun={(run) => applyPreviousRun(run, 'perfect')}
      onSelectPartialRun={(run) => applyPreviousRun(run, 'partial')}
    />
  );
};

export default ManualRunModal;
